import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';

import { extractDateFromISO, formatDate, formatDateDisplay, formatDateTimeWorded } from './format';
import { subtractTime } from './manipulate';
import { storedDayAsLocalDate } from './timezone';

/**
 * A calendar day as the API sends one, `yyyy-MM-dd` or the UTC midnight of a date column, in
 * the standard display day (`dd/MM/yyyy`). The day stays the one stored in every browser zone,
 * where reading UTC midnight as an instant would show the day before west of UTC. "" without
 * a day.
 *
 * @example
 * formatDayDisplay('2026-09-23'); // "23/09/2026"
 * formatDayDisplay('2026-09-23T00:00:00.000Z'); // "23/09/2026", in Santiago too
 */
export function formatDayDisplay(day: string | null | undefined): string {
    return formatDateDisplay(storedDayAsLocalDate(day));
}

export interface LocalizedDateFormatProps {
    /** No date (undefined or "") formats as "". */
    dateString: string | undefined;
    /**
     * Accepted for backward compatibility; the page language decides. A day is dd/MM/yyyy and
     * a date with its time the worded date-time (`formatDateTimeWorded`).
     */
    locale?: string;
    onlydate?: boolean;
}

/**
 * The standard display day (`dd/MM/yyyy`) when `onlydate`, otherwise the worded date-time to
 * the second; "" without a date.
 */
export function localizedDateFormat({
    dateString,
    onlydate = false,
}: LocalizedDateFormatProps): string {
    if (!dateString) return '';
    return onlydate ? formatDateDisplay(dateString) : formatDateTimeWorded(dateString);
}

/** The standard display date (`dd/MM/yyyy`) of a date string. */
export function localizedDate(dateString: string, locale?: string): string {
    return localizedDateFormat({ dateString, locale, onlydate: true });
}

/**
 * A date range for a page: the days `inputDateObject` gives, otherwise the 28 days up to
 * the reference ISO date (e.g. the last measurement).
 */
export function computeDateRange(
    referenceISODate: string | undefined,
    inputDateObject?: DateRange
): { dateFrom: string; dateTo: string } {
    const referenceDate = extractDateFromISO(referenceISODate);
    const defaultDateFrom = formatDate(subtractTime(referenceDate, 28, 'day'), 'internal');

    const dateFrom = inputDateObject?.dateFrom ?? defaultDateFrom;
    const dateTo = inputDateObject?.dateTo ?? referenceDate;

    return { dateFrom, dateTo };
}
